<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'userList' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Qualifikationen - {{ userData?.firstName }} {{ userData?.lastName }} ({{
            userData?.engagementTypeTranslation
          }})
        </h6>

        <CreateButton :label="$t('user.create')" :to="{ name: 'userForm' }"/>
      </div>

      <div class="element-box">
        <UserMenu :userId="userId" :employeeType="employeeType"/>

        <div class="row align-items-center justify-content-between mt-4 ml-1">
          <ExcelExportButton
              style="margin-right: 15px;"
              :label="$t('general.excel_export')"
              :loading="isExcelLoaded"
              @click="getExcelExport"/>

          <button type="button" class="btn btn-outline-primary" @click="handleFormQualification(null)">
            Qualifikationen hinzufügen
          </button>
        </div>

        <EmptyState
            v-if="!qualifications.length"
            icon="fa-certificate"
            title="Keine Qualifikationen gefunden"/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th>Art</th>
              <th class="text-center">Nachweis vorhanden</th>
              <th style="text-align: center;  width: 50px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="qualification in qualifications"
                :key="qualification.id"
                :class="['table-row', { 'bg-light-gray': qualification.note }]"
                @click="!isCoordinatorButNotHr && handleFormQualification(qualification.id)">

              <td>{{ qualification?.type }}</td>

              <td class="text-center">
                {{ String(qualification?.proofAvailable) === '1' ? 'Ja' : 'Nein' }}
              </td>

              <td class="text-center">
                <button v-if="!isCoordinatorButNotHr"
                        class="btn btn-danger ml-2"
                        @click.stop="handleDeleteQualification(qualification?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Modal
        v-model="showModalForm"
        title="Qualifikationen"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-xl"
        :hide-footer="true"
        classFooter="justify-content-center">
      <Form :userId="userId"
            :qualificationId="qualificationId"
            v-model:submitForm="submitForm"/>
    </Modal>

    <ModalDelete
        v-model="showModalDelete"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteQualification">
    </ModalDelete>
  </PageTemplate>
</template>


<script setup>
import userService from '@/api/User';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {downloadFile} from '@/shared/functions';
import Form from '@/views/App/User/Qualification/Form.vue';
import UserMenu from '@/views/App/User/UserMenu.vue';
import {onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const route = useRoute();
const userId = route.params.id;
const {t} = useI18n();
const userData = ref(null);
const qualifications = ref([]);
const qualificationId = ref(null);
const showModalForm = ref(false);
const showModalDelete = ref(false);
const isLoaded = ref(false);
const submitForm = ref(false);
const isCoordinatorButNotHr = ref(false);
const employeeType = ref("");
const isExcelLoaded = ref(false);

watch(submitForm, () => {
  showModalForm.value = false;
  showModalDelete.value = false;
  qualificationId.value = null;
  getQualifications();
});

async function getQualifications(page = 1, perPage, passedFilters) {
  CloseToast();
  try {
    const {data} = await userService.getQualifications(+userId);

    qualifications.value = data.list;
    isCoordinatorButNotHr.value = data.isCoordinatorButNotHr;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getUser() {
  try {
    const {data} = await userService.getUser(+userId);
    userData.value = data?.data?.user;
    employeeType.value = data?.data?.user?.employeeType

  } catch (error) {
    await ErrorToast(error);
  }
}

function handleDeleteQualification(id) {
  CloseToast();
  showModalForm.value = false;
  CloseToast();
  qualificationId.value = id;
  showModalDelete.value = true;
}

async function confirmDeleteQualification() {
  try {
    await userService.deleteQualification(qualificationId.value);
    showModalForm.value = false;
    showModalDelete.value = false;
    await getQualifications();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleFormQualification(id) {
  CloseToast();
  showModalForm.value = false;
  CloseToast();
  qualificationId.value = id;
  showModalDelete.value = false;
  showModalForm.value = true;
}

async function getExcelExport() {
  await downloadFile('/user/qualification-excel-export/${user.id}', undefined,
      null,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    await getQualifications(),
    await getUser(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>